/**
 * SystemBackPort — the DEVICE Back gesture, routed into the app's own history.
 *
 * This is the ports layer (ARCHITECTURE §3, CLAUDE.md §8): `src/platform/` is
 * the only place `@capacitor/*` may be imported, and feature code asks for the
 * capability rather than for the plugin.
 *
 * WHY THIS PORT EXISTS AT ALL — the finding that produced it (M10 / S10-03B,
 * Android acceptance round four). On Jerry's phone the edge-back gesture closed
 * Crewlink to the Home screen and destroyed an in-progress report. The cause
 * was NOT the web history, which was already correct: it was that nothing was
 * listening for the system Back at all.
 *
 *   * `@capacitor/android` 8.5.0 contains no back handling of any kind — no
 *     `onBackPressed`, no `OnBackInvokedCallback`, no `webView.goBack()`.
 *   * `@capacitor/app` was present in `node_modules` only as a transitive
 *     dependency of the secure-storage plugin, so the CLI never synced its
 *     native module: `capacitor.plugins.json` listed one plugin, not two.
 *   * With `targetSdkVersion 36` and no registered callback, Android's own
 *     dispatcher finishes the Activity. The WebView is destroyed with it, and
 *     every in-memory selection dies.
 *
 * A CORRECTION THIS FILE EXISTS TO CARRY, because it was written into tests and
 * comments elsewhere and was wrong: "no back handler is installed, so Android
 * Back is simply browser history Back." **No handler does not mean the WebView
 * receives Back — it means nothing receives it and the app closes.**
 *
 * THE PLUGIN'S OWN SEMANTICS, read from
 * `node_modules/@capacitor/app/android/.../AppPlugin.java`, because the policy
 * below only makes sense against them:
 *
 *   * The plugin registers an `OnBackPressedCallback` that is ENABLED by
 *     default, so once it is installed the Activity no longer finishes on its
 *     own. Back is ours to answer.
 *   * With NO JavaScript listener it calls `webView.goBack()` when the WebView
 *     can go back — and **does nothing at all when it cannot.** That is a trap:
 *     a user at the root could never leave the app. Registering a listener is
 *     therefore not optional once the plugin is installed; it is the thing that
 *     keeps Back from becoming a dead key.
 *   * With a listener it hands over `canGoBack` and does nothing itself. Every
 *     decision — including leaving the app — becomes ours.
 *
 * ONE CODE PATH, NOT A PLATFORM BRANCH IN THE CALLER: the caller always
 * subscribes, and the WEB implementation is the one that decides to do nothing.
 * A component asking "am I on a device?" before subscribing would put the
 * platform question back in feature code, which is the exact thing §8 forbids.
 *
 * WHAT THIS PORT DELIBERATELY DOES NOT DO: it holds no navigation policy. It
 * delivers the event and can exit the app. Deciding what Back MEANS is
 * `src/app/system-back-policy.ts`, which is pure and therefore provable.
 */

import { App } from '@capacitor/app';
import { detectPlatform } from './session-storage';

/** What the shell tells us when the user presses Back. */
export interface SystemBackEvent {
  /**
   * Whether the WEB VIEW believes it has somewhere to go back to. Reported by
   * the shell, used only as a fallback: the router's own index is the better
   * answer when it is available (see `system-back-policy.ts`).
   */
  readonly canGoBack: boolean;
}

export type SystemBackHandler = (event: SystemBackEvent) => void;

/** Removes a previously registered handler. Safe to call more than once. */
export type SystemBackUnsubscribe = () => void;

export interface SystemBackPort {
  /** Human-readable implementation name. Diagnostics only, never product UI. */
  readonly name: string;
  /**
   * Register the handler. Resolves with its remover. Registering twice
   * registers twice, so callers unsubscribe on unmount — a duplicated handler
   * would consume two history entries per press.
   */
  subscribe(handler: SystemBackHandler): Promise<SystemBackUnsubscribe>;
  /**
   * Leave the app, the way a normal Android app leaves when Back is pressed at
   * its root. A no-op where that has no meaning. Never rejects.
   */
  exitApp(): Promise<void>;
}

/**
 * The browser implementation: DO NOTHING, on purpose.
 *
 * A browser already has a Back button and a history stack, and the page is a
 * guest in it. Intercepting Back in a tab would break the tab's own navigation,
 * and a web page cannot — and must not — close the window it is in. So the web
 * implementation never calls the handler and never exits: the platform's
 * behaviour IS the correct behaviour.
 */
export function createWebSystemBack(): SystemBackPort {
  return {
    name: 'web:none',
    subscribe() {
      return Promise.resolve(() => {
        // Nothing was registered, so nothing is removed. Callers may always
        // unsubscribe without asking which implementation they were given.
      });
    },
    exitApp() {
      return Promise.resolve();
    },
  };
}

/** The slice of `@capacitor/app` this adapter uses. Structural, so the whole
 * adapter is provable without a device or the real plugin. */
export interface AppPluginLike {
  addListener(
    eventName: 'backButton',
    listener: (event: { canGoBack?: boolean }) => void,
  ): Promise<{ remove: () => Promise<void> }>;
  exitApp(): Promise<void>;
}

/**
 * The native implementation, on top of the official plugin.
 *
 * RACE SAFETY, and it is not hypothetical: `addListener` is asynchronous, and a
 * React effect can be torn down before it resolves (StrictMode does exactly
 * that on every mount in development, and HMR does it constantly). If the
 * remover were simply dropped, every remount would leave another live listener
 * behind and a single Back press would eventually pop several history entries.
 * So unsubscribing before the handle arrives is recorded, and the handle is
 * removed the moment it exists.
 */
export function createCapacitorSystemBack(plugin: AppPluginLike): SystemBackPort {
  return {
    name: 'capacitor:@capacitor/app',

    async subscribe(handler) {
      let removed = false;
      let handle: { remove: () => Promise<void> } | null = null;

      const remove = (): void => {
        if (removed) return;
        removed = true;
        // Fire-and-forget by design: unsubscribe is called from an effect
        // cleanup, which cannot await, and a failed removal has no recovery
        // beyond not crashing the teardown.
        void handle?.remove().catch(() => {
          // The listener is already gone, or the bridge is tearing down. Either
          // way the outcome we wanted has happened.
        });
      };

      try {
        handle = await plugin.addListener('backButton', (event) => {
          // A removed listener that still fires must not be answered — that is
          // the duplicate-history-pop bug the removal exists to prevent.
          if (removed) return;
          handler({ canGoBack: event.canGoBack === true });
        });
      } catch {
        // The plugin is unavailable in this runtime. The honest outcome is that
        // no handler is registered; Back then behaves as the shell decides,
        // which is what happened before this port existed.
        return () => {
          removed = true;
        };
      }

      // NO "unsubscribed while registering" BRANCH HERE, DELIBERATELY. The
      // caller cannot have unsubscribed yet: the remover it would use is what
      // this promise resolves WITH. That race is real, but it belongs one level
      // up — `SystemBackBridge` can be torn down before this resolves, and it
      // is where the teardown flag lives. A guard here could never run, and a
      // check that can never run reads as protection while providing none.
      return remove;
    },

    async exitApp() {
      try {
        await plugin.exitApp();
      } catch {
        // Nothing useful is left to do if the shell refuses to close, and
        // throwing out of a Back handler would be worse than staying open.
      }
    },
  };
}

/**
 * Chosen once, like every other port (ARCHITECTURE §3).
 *
 * FAIL SAFE MEANS "DO NOT INTERCEPT". Only a CONFIRMED native platform gets the
 * plugin. `unknown` — the three-valued answer `detectPlatform()` returns when
 * the platform question itself throws — takes the web implementation, because
 * the cost of not intercepting on a device is the behaviour we had before this
 * slice, while the cost of intercepting in a browser is breaking the browser's
 * own Back button for every user.
 */
export function selectSystemBack(): SystemBackPort {
  return detectPlatform() === 'native' ? createCapacitorSystemBack(App) : createWebSystemBack();
}
